import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  listProjectWebhookDeliveries,
  retryProjectWebhookDelivery,
} from '../../utils/api';
import ButtonComponent from '../customButton';
import ChipComponent from '../chips/ChipComponent/ChipComponent';
import { getSemanticVariant } from '../chips/ChipComponent/chipVariants';
import TableComponent from '../TableComponent';
import styles from './DeliveryLogTab.module.scss';

const DELIVERY_LOG_COPY = {
  heading: 'Webhook delivery log',
  description: 'Review recent webhook delivery attempts for this project.',
  event: 'Event',
  status: 'Status',
  timestamp: 'Timestamp',
  retryCount: 'Retry count',
  actions: 'Actions',
  retry: 'Retry',
  empty: 'No webhook deliveries have been recorded.',
  loadError: 'Unable to load webhook deliveries.',
  retryError: 'Unable to retry this webhook delivery.',
  tryAgain: 'Try again',
  unavailable: '-',
};

const RETRY_REFRESH_DELAY = 1500;
const DEFAULT_PAGINATION_MODEL = { page: 0, pageSize: 25 };

const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const formatStatusLabel = (status) => (
  typeof status === 'string' && status.length > 0
    ? `${status.charAt(0).toUpperCase()}${status.slice(1)}`
    : DELIVERY_LOG_COPY.unavailable
);

const getDeliveryStatusVariant = (status) => {
  switch (status) {
    case 'pending':
      return getSemanticVariant('warning');
    case 'dispatched':
    case 'delivered':
    case 'sent':
    case 'success':
      return getSemanticVariant('success');
    case 'failed':
      return getSemanticVariant('error');
    default:
      return getSemanticVariant('neutral');
  }
};

const getEventKey = (delivery) => (
  delivery?.NotificationEvent?.event_key
  || delivery?.notificationEvent?.event_key
  || delivery?.event?.event_key
  || delivery?.event_key
  || DELIVERY_LOG_COPY.unavailable
);

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return DELIVERY_LOG_COPY.unavailable;

  return date.toLocaleString();
};

const DeliveryLogTab = ({ projectId }) => {
  const [deliveries, setDeliveries] = useState([]);
  const [total, setTotal] = useState(0);
  const [paginationModel, setPaginationModel] = useState(DEFAULT_PAGINATION_MODEL);
  const [isLoading, setIsLoading] = useState(true);
  const [retryingId, setRetryingId] = useState(null);
  const [error, setError] = useState('');
  const refreshTimerRef = useRef(null);

  const loadDeliveries = useCallback(async () => {
    if (!projectId) {
      setDeliveries([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError('');
    try {
      const data = await listProjectWebhookDeliveries(projectId, {
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
      });
      setDeliveries(data?.items || []);
      setTotal(data?.total || 0);
    } catch (requestError) {
      setError(getErrorMessage(requestError, DELIVERY_LOG_COPY.loadError));
    } finally {
      setIsLoading(false);
    }
  }, [projectId, paginationModel.page, paginationModel.pageSize]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  useEffect(() => () => {
    if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
  }, [projectId]);

  const retryDelivery = async (deliveryId) => {
    if (retryingId) return;

    setRetryingId(deliveryId);
    setError('');
    setDeliveries((currentDeliveries) => currentDeliveries.map((delivery) => (
      delivery.id === deliveryId ? { ...delivery, status: 'pending' } : delivery
    )));

    try {
      await retryProjectWebhookDelivery(deliveryId, projectId);
      refreshTimerRef.current = window.setTimeout(() => {
        loadDeliveries();
        refreshTimerRef.current = null;
      }, RETRY_REFRESH_DELAY);
    } catch (requestError) {
      setDeliveries((currentDeliveries) => currentDeliveries.map((delivery) => (
        delivery.id === deliveryId ? { ...delivery, status: 'failed' } : delivery
      )));
      setError(getErrorMessage(requestError, DELIVERY_LOG_COPY.retryError));
    } finally {
      setRetryingId(null);
    }
  };

  if (error && deliveries.length === 0 && !isLoading) {
    return (
      <div className={`${styles.status} ${styles.error}`} role="alert">
        <p>{error}</p>
        <ButtonComponent
          ctaText={DELIVERY_LOG_COPY.tryAgain}
          variant="secondary"
          size="small"
          onClick={loadDeliveries}
        />
      </div>
    );
  }

  const columns = [
    {
      field: 'eventKey',
      headerName: DELIVERY_LOG_COPY.event,
      flex: 1,
      minWidth: 200,
      valueGetter: (_, row) => getEventKey(row),
    },
    {
      field: 'status',
      headerName: DELIVERY_LOG_COPY.status,
      minWidth: 140,
      renderCell: (params) => {
        const status = formatStatusLabel(params.value);
        return (
          <ChipComponent variant={getDeliveryStatusVariant(params.value)} size="small">
            {status}
          </ChipComponent>
        );
      },
    },
    {
      field: 'created_at',
      headerName: DELIVERY_LOG_COPY.timestamp,
      minWidth: 200,
      flex: 1,
      valueGetter: (_, row) => formatTimestamp(row.created_at || row.createdAt),
    },
    {
      field: 'retry_count',
      headerName: DELIVERY_LOG_COPY.retryCount,
      minWidth: 130,
      valueGetter: (_, row) => row.retry_count ?? row.retryCount ?? 0,
    },
    {
      field: 'actions',
      headerName: DELIVERY_LOG_COPY.actions,
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        const isFailed = params.row.status === 'failed';
        const isRetrying = retryingId === params.row.id;

        return isFailed ? (
          <ButtonComponent
            ctaText={DELIVERY_LOG_COPY.retry}
            variant="outline"
            size="small"
            onClick={() => retryDelivery(params.row.id)}
            disabled={Boolean(retryingId)}
            loading={isRetrying}
          />
        ) : null;
      },
    },
  ];

  return (
    <section className={styles.deliveryLog} aria-labelledby="delivery-log-heading">
      <header className={styles.header}>
        <h2 id="delivery-log-heading">{DELIVERY_LOG_COPY.heading}</h2>
        <p>{DELIVERY_LOG_COPY.description}</p>
      </header>

      {error && deliveries.length > 0 && <p className={styles.error} role="alert">{error}</p>}

      <div className={styles.tableWrapper}>
        <TableComponent
          title=""
          isHeaderContentVisible={false}
          hideColumnsVisibilityButton
          rows={deliveries}
          columns={columns}
          idField="id"
          viewType="webhookDeliveryLog"
          loading={isLoading}
          isPagination
          paginationMode="server"
          rowCount={total}
          paginationModel={paginationModel}
          isCustomPagination
          handleCustomPaginationPageChange={(event, page) => setPaginationModel((current) => ({ ...current, page }))}
          handleCustomPaginationPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
          isData={isLoading || deliveries.length > 0}
          customNoData={<p className={styles.empty}>{DELIVERY_LOG_COPY.empty}</p>}
          tableHeight="100%"
        />
      </div>
    </section>
  );
};

export default DeliveryLogTab;
